<template>
    <BaseButton
        v-bind="props"
        :class="[
            $style[`color_${props.color}_override`],
            $style.outline_button_override,
            sizesStyles[`size_${props.size}`],
        ]"
    >
        <Icon
            v-if="props.leftIcon"
            :name="props.leftIcon"
            :class="sizesStyles.left_icon"
        />
        <Typography
            v-if="props.label"
            type="label"
            :responsive="false"
            v-bind="labelBindings"
            :class="$style.label_override"
        >
            {{ props.label }}
        </Typography>
        <slot />
    </BaseButton>
</template>

<script lang="ts" setup>
import { type HTMLAttributes } from 'vue'

import { type IconNameType } from '@/components/Icon'
import Icon from '@/components/Icon/Icon.vue'
import Typography from '@/components/Typography.vue'

import BaseButton, { type BaseButtonProps } from './components/BaseButton.vue'
import { useLabelBindings } from './composables/use-label-bindings'
import sizesStyles from './styles/sizes.module.css'

type Props = {
    leftIcon?: IconNameType
    label?: string | number
} & BaseButtonProps &
    /* @vue-ignore */ Omit<HTMLAttributes, 'hidden'>

const props = withDefaults(defineProps<Props>(), {
    type: 'button',
    size: 'm',
    width: 'normal',
    color: 'purple-400',
})

const { labelBindings } = useLabelBindings(props.size)
</script>
<style module>
.color_purple-400_override {
    --main-outline: #c7abff;
    --main-outline-text: #c7abff;
}

.color_purple-400-flat_override {
}

.color_purple-500_override {
    --main-outline: #c7abff;
    --main-outline-text: #c7abff;
}

.color_purple-600_override {
    --main-outline: #c7abff;
    --main-outline-text: #c7abff;
}

.color_purple-graphite_override {
    --main-outline: #c7abff;
    --main-outline-text: #c7abff;
}

.color_golden_override {
}

.color_white_override {
    --main-outline: rgba(255, 255, 255, 0.2);
    --main-outline-disabled: rgba(255, 255, 255, 0.2);
    --main-outline-text: white;
}

.color_white-08_override {
}

.color_white-60_override {
    --main-outline: rgba(255, 255, 255, 0.6);
    --main-outline-text: rgba(255, 255, 255, 0.6);
}

.color_white-20_override {
    --main-outline: rgba(255, 255, 255, 0.2);
    --main-outline-disabled: rgba(255, 255, 255, 0.2);
    --main-outline-text: white;
}

.color_carbon-200_override {
}

.color_carbon-300_override {
}

.color_carbon-400_override {
}

.color_lime_override {
}

.color_lavander-300_override {
}

.color_rose-100_override {
}

.color_rose-400_override {
}

.color_salad_override {
    --main-outline: #9ce52e;
    --main-outline-text: #9ce52e;
}

.outline_button_override {
    --border-width: 1px;
    color: var(--main-outline-text);
    --border-color: var(--main-outline);
}

.outline_button_override:not(:disabled):hover,
.outline_button_override:not(:disabled):active {
    color: var(--text);
    --background-color: var(--main);
    --border-color: transparent;
}

.outline_button_override:not(:disabled):hover:before {
    filter: brightness(1.08);
}

.outline_button_override:not(:disabled):active:before {
    filter: brightness(0.92);
}

.outline_button_override:disabled {
    color: var(--white-40);
    --border-color: var(--carbon-400);
}

.label_override {
    line-height: 100%;
}
</style>
